import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Plus, Trash2, Edit, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const TimeSlotsManagement = () => {
  const API = getBackendUrl() || '';
  
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    start_time: '',
    end_time: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTimeSlots();
  }, []);

  const fetchTimeSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/time-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[TIME SLOTS] API response:', response.data);
      
      // Combine default slots with custom slots
      const defaultSlots = [
        { id: 'default-1', name: '09:00-10:30', start_time: '09:00', end_time: '10:30', duration_hours: 1.5, is_default: true },
        { id: 'default-2', name: '10:30-12:00', start_time: '10:30', end_time: '12:00', duration_hours: 1.5, is_default: true },
        { id: 'default-3', name: '12:00-13:30', start_time: '12:00', end_time: '13:30', duration_hours: 1.5, is_default: true },
        { id: 'default-4', name: '13:30-15:00', start_time: '13:30', end_time: '15:00', duration_hours: 1.5, is_default: true },
        { id: 'default-5', name: '15:00-16:30', start_time: '15:00', end_time: '16:30', duration_hours: 1.5, is_default: true },
        { id: 'default-6', name: '16:30-18:00', start_time: '16:30', end_time: '18:00', duration_hours: 1.5, is_default: true }
      ];
      
      const combinedSlots = [...defaultSlots, ...response.data];
      console.log('[TIME SLOTS] Combined slots:', combinedSlots);
      
      setTimeSlots(combinedSlots);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      
      // Even if API fails, show default slots
      const defaultSlots = [
        { id: 'default-1', name: '09:00-10:30', start_time: '09:00', end_time: '10:30', duration_hours: 1.5, is_default: true },
        { id: 'default-2', name: '10:30-12:00', start_time: '10:30', end_time: '12:00', duration_hours: 1.5, is_default: true },
        { id: 'default-3', name: '12:00-13:30', start_time: '12:00', end_time: '13:30', duration_hours: 1.5, is_default: true },
        { id: 'default-4', name: '13:30-15:00', start_time: '13:30', end_time: '15:00', duration_hours: 1.5, is_default: true },
        { id: 'default-5', name: '15:00-16:30', start_time: '15:00', end_time: '16:30', duration_hours: 1.5, is_default: true },
        { id: 'default-6', name: '16:30-18:00', start_time: '16:30', end_time: '18:00', duration_hours: 1.5, is_default: true }
      ];
      
      console.log('[TIME SLOTS] API failed, using default slots only');
      setTimeSlots(defaultSlots);
      setLoading(false);
      
      toast.info('Using Default Time Slots - Could not fetch custom time slots, showing 6 default slots');
    }
  };

  const calculateDuration = (start, end) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return ((endMinutes - startMinutes) / 60).toFixed(1);
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      console.log('[TIME SLOTS] Already submitting, ignoring click');
      return;
    }
    
    console.log('[TIME SLOTS] handleSubmit called', formData);
    setIsSubmitting(true);
    
    try {
      if (!formData.name || !formData.start_time || !formData.end_time) {
        toast.error('All fields are required');
        setIsSubmitting(false);
        return;
      }

      const token = localStorage.getItem('token');
      const duration = calculateDuration(formData.start_time, formData.end_time);

      const timeSlotData = {
        name: formData.name,
        start_time: formData.start_time,
        end_time: formData.end_time,
        duration_hours: parseFloat(duration),
        is_active: true
      };

      console.log('[TIME SLOTS] Sending data:', timeSlotData);

      if (editingSlot) {
        await axios.put(`${API}/api/time-slots/${editingSlot.id}`, timeSlotData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[TIME SLOTS] Update successful');
        toast.success('Time slot updated successfully');
      } else {
        const response = await axios.post(`${API}/api/time-slots`, timeSlotData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[TIME SLOTS] Create response:', response.data);
        toast.success('Time slot created successfully');
      }

      // Close modal and refresh
      setShowAddModal(false);
      setEditingSlot(null);
      setFormData({ name: '', start_time: '', end_time: '' });
      setIsSubmitting(false);
      
      // Force refresh after a short delay to ensure state is updated
      setTimeout(() => {
        fetchTimeSlots();
      }, 300);
      
    } catch (error) {
      console.error('[TIME SLOTS] Error saving time slot:', error);
      console.error('[TIME SLOTS] Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save time slot');
      setIsSubmitting(false);
    }
  };

  const handleEdit = (slot) => {
    if (slot.is_default) {
      toast.error('Cannot edit default time slots');
      return;
    }
    setEditingSlot(slot);
    setFormData({
      name: slot.name,
      start_time: slot.start_time,
      end_time: slot.end_time
    });
    setShowAddModal(true);
  };

  const handleDelete = async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this time slot?')) {
      return;
    }
    
    if (isSubmitting) {
      console.log('[TIME SLOTS] Already processing, ignoring delete');
      return;
    }
    
    setIsSubmitting(true);
    console.log('[TIME SLOTS] Deleting slot:', slotId);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/time-slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[TIME SLOTS] Delete successful');
      toast.success('Time slot deleted successfully');
      
      // Refresh after short delay
      setTimeout(() => {
        setIsSubmitting(false);
        fetchTimeSlots();
      }, 300);
      
    } catch (error) {
      console.error('[TIME SLOTS] Error deleting time slot:', error);
      toast.error('Failed to delete time slot');
      setIsSubmitting(false);
    }
  };

  const handleBulkCleanup = async () => {
    if (!window.confirm('⚠️ WARNING: This will deactivate ALL custom time slots. Default slots will remain. Are you sure?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/time-slots/cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Cleanup Completed',
        description: `Deactivated ${response.data.slots_deactivated} custom time slots`,
        variant: 'default'
      });
      
      fetchTimeSlots();
    } catch (error) {
      console.error('Error during cleanup:', error);
      toast({
        title: 'Cleanup Failed',
        description: error.response?.data?.detail || 'Failed to cleanup time slots',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({ name: '', start_time: '', end_time: '' });
    setEditingSlot(null);
    setShowAddModal(false);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Clock className="w-8 h-8 mr-3 text-blue-600" />
            Time Slots Management
          </h1>
          <p className="text-gray-600 mt-2">Manage class time slots for scheduling</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleBulkCleanup} 
            variant="destructive"
            className="flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Cleanup All Custom Slots</span>
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Custom Time Slot</span>
          </Button>
        </div>
      </div>

      {/* Time Slots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8">
            <p className="text-gray-600">Loading time slots...</p>
          </div>
        ) : (
          timeSlots.map((slot) => (
            <Card key={slot.id} className={slot.is_default ? 'bg-blue-50 border-blue-200' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-lg text-gray-900">{slot.name}</h3>
                      {slot.is_default && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Default</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {slot.start_time} - {slot.end_time}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Duration: {slot.duration_hours} {slot.duration_hours === 1 ? 'hour' : 'hours'}
                    </p>
                  </div>
                  {!slot.is_default && (
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(slot)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(slot.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">About Time Slots</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• Default 1.5-hour time slots are provided and cannot be edited or deleted</li>
            <li>• You can add custom time slots for hourly classes, workshops, or special sessions</li>
            <li>• Custom time slots will be available when creating course assignments</li>
            <li>• Duration is automatically calculated based on start and end times</li>
            <li>• Existing sessions using deleted time slots will not be affected</li>
          </ul>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingSlot ? 'Edit Time Slot' : 'Add Custom Time Slot'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Time Slot Name</Label>
              <Input
                placeholder="e.g., 1 Hour Morning Slot"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>
            {formData.start_time && formData.end_time && (
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Duration:</strong> {calculateDuration(formData.start_time, formData.end_time)} hours
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={resetForm}
              type="button"
            >
              Cancel
            </Button>
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[TIME SLOTS] Button clicked!');
                handleSubmit();
              }}
              type="button"
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : (editingSlot ? 'Update' : 'Add')} Time Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeSlotsManagement;
